// App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';

import Layout from './components/Layout';
import DashboardLayout from './components/DashboardLayout';
import FormateurLayout from './components/FormateurLayout';
import AdminLayout from './components/AdminLayout';
import RoleBasedRoute from './components/RoleBasedRoute';
import AuthCallback from './pages/auth/AuthCallback';

import HomePage from './pages/HomePage';
import ContactPage from './pages/ContactPage';
import AboutUsPage from './pages/AboutUsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import DataDeletionPage from './pages/DataDeletionPage';

import CoursesPage from './pages/courses/CoursesPage';
import CourseDetailsPage from './pages/courses/CourseDetailsPage';

import SignInPage from './pages/auth/SignInPage';
import SignUpPage from './pages/auth/SignUpPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// User Dashboard
import DashboardUser from './pages/UserDashboard/DashboardUser';
import DashboardAbout from './pages/UserDashboard/DashboardAbout';
import DashboardCourses from './pages/UserDashboard/DashboardCourses';
import DashboardProfile from './pages/UserDashboard/DashboardProfile';
import DashboardWatch from './pages/UserDashboard/DashboardWatch';

// Formateur Dashboard
import DashboardFormateur from './pages/FormateurDashboard/DashboardFormateur';
import DashboardAboutFr from './pages/FormateurDashboard/DashboardAbout';
import DashboardProfileFr from './pages/FormateurDashboard/DashboardProfile';
import FormateurCourses from './pages/FormateurDashboard/FormateurCourses';
import FormateurAddCourse from './pages/FormateurDashboard/FormateurAddCourse';
import FormateurEditCourse from './pages/FormateurDashboard/FormateurEditCourse';

// Admin Dashboard
import DashboardAdmin from './pages/AdminDashboard/DashboardAdmin';
import AdminUsers from './pages/AdminDashboard/AdminUsers';
import AdminUserDetails from './pages/AdminDashboard/AdminUserDetails';
import AdminCourses from './pages/AdminDashboard/AdminCourses';
import AdminAddCourse from './pages/AdminDashboard/AdminAddCourse';
import AdminEditCourse from './pages/AdminDashboard/AdminEditCourse';
import AdminInscriptions from './pages/AdminDashboard/AdminInscriptions';
import AdminAddInscription from './pages/AdminDashboard/AdminAddInscription';

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes with Layout (authenticated and non-authenticated) */}
          <Route element={<Layout><Outlet /></Layout>}>
            <Route path="/" element={<HomePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/data-deletion" element={<DataDeletionPage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/:id" element={<CourseDetailsPage />} />
          </Route>

          {/* Authentication Routes */}
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* ✅ FIXED: User Dashboard Routes - ONLY for users, admins redirected to /admin */}
          <Route path="/dashboard" element={
            <RoleBasedRoute allowedRoles={['user']}>
              <DashboardLayout>
                <Outlet />
              </DashboardLayout>
            </RoleBasedRoute>
          }>
            <Route index element={<DashboardUser />} />
            <Route path="about" element={<DashboardAbout />} />
            <Route path="courses" element={<DashboardCourses />} />
            <Route path="profile" element={<DashboardProfile />} />
          </Route>

          {/* ✅ FIXED: formateur Dashboard Routes - ONLY for formateur, admins redirected to /admin */}
          <Route path="/formateur" element={
            <RoleBasedRoute allowedRoles={['formateur']}>
              <FormateurLayout>
                <Outlet />
              </FormateurLayout>
            </RoleBasedRoute>
          }>
            <Route index element={<DashboardFormateur />} />
            <Route path="about" element={<DashboardAboutFr />} />
            <Route path="courses" element={<FormateurCourses />} />
            <Route path="profile" element={<DashboardProfileFr />} />
            <Route path="courses/new" element={<FormateurAddCourse />} />
            <Route path="courses/:id" element={<FormateurEditCourse />} />
          </Route>

          {/* ✅ FIXED: Individual User Routes - ONLY for users */}
          <Route path="/dashboard/watch/:id" element={
            <RoleBasedRoute allowedRoles={['user']}>
              <DashboardWatch />
            </RoleBasedRoute>
          } />

          {/* ✅ FIXED: Admin Dashboard Routes - ONLY for admins */}
          <Route path="/admin" element={
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminLayout>
                <Outlet />
              </AdminLayout>
            </RoleBasedRoute>
          }>
            <Route index element={<DashboardAdmin />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:id" element={<AdminUserDetails />} />
            <Route path="courses" element={<AdminCourses />} />
            <Route path="courses/new" element={<AdminAddCourse />} />
            <Route path="courses/:id" element={<AdminEditCourse />} />
            <Route path="inscriptions" element={<AdminInscriptions />} />
            <Route path="inscriptions/new" element={<AdminAddInscription />} />
          </Route>

          {/* Default redirects based on authentication status */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
